import { Link, useParams } from "react-router-dom";

import {
  cancelOpByIdPoll,
  fetchApp,
  fetchImageById,
  fetchOperationById,
  fetchOperationLogs,
  pollOperationById,
  selectAppById,
  selectImageById,
  selectOperationById,
} from "@app/deploy";
import {
  fetchDeploymentsByAppId,
  selectDeploymentsByAppId,
} from "@app/deployment";
import {
  cancelActivityByIdPoll,
  cancelResourceByTypePoll,
  idForTargetResourceType,
  pollActivityById,
  pollResourceByTypeThunk,
  selectFreezerActivityById,
} from "@app/freezer";
import { useDispatch, useQuery, useSelector } from "@app/react";
import { appDetailUrl, sourceDetailUrl } from "@app/routes";
import { schema } from "@app/schema";
import { fetchSourceById, selectSourceById } from "@app/source";
import { Fragment, useEffect, useMemo, useState } from "react";
import { usePoller } from "../hooks";
import {
  Box,
  ButtonIcon,
  ButtonLinkExternal,
  DockerImage,
  GitCommitMessage,
  GitRef,
  IconDownload,
  LoadingSpinner,
  LogViewer,
  ResourceDetailItem,
  tokens,
} from "../shared";
import { JsonView } from "../shared/json-view";

const APP_DEPLOYMENT_ACTIVITY_TYPES = new Set(["app.deploy", "app.configure"]);

function DeploymentDetailsWidget({
  appId,
  operationId,
}: {
  appId: string;
  operationId: string;
}) {
  useQuery(fetchApp({ id: appId }));
  const app = useSelector((s) => selectAppById(s, { id: appId }));

  useQuery(fetchDeploymentsByAppId({ id: appId }));
  const deployments = useSelector((s) =>
    selectDeploymentsByAppId(s, { appId }),
  );
  const deployment = deployments.find((d) => d.operationId === operationId);

  useQuery(fetchImageById({ id: deployment?.imageId ?? "" }));
  const image = useSelector((s) =>
    selectImageById(s, { id: deployment?.imageId ?? "" }),
  );

  useQuery(fetchSourceById({ id: deployment?.sourceId ?? "" }));
  const source = useSelector((s) =>
    selectSourceById(s, { id: deployment?.sourceId ?? "" }),
  );

  if (!deployment) return null;

  return (
    <Box size="md" className="flex flex-col gap-2">
      <h3 className={tokens.type["micro label strong"]}>Deployment Details</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-black-100">
        <div className="flex flex-col lg:pr-4 [&>*:not(:first-child)]:border-t [&>*:not(:first-child)]:border-black-100">
          <ResourceDetailItem title="App">
            <Link to={appDetailUrl(app.id)} className="text-indigo">
              {app.handle}
            </Link>
          </ResourceDetailItem>
          <ResourceDetailItem title="Git Ref">
            <GitRef
              gitRef={deployment.gitRef}
              commitSha={deployment.gitCommitSha}
              commitUrl={deployment.gitCommitUrl}
            />
          </ResourceDetailItem>
          <ResourceDetailItem title="Commit Message">
            <GitCommitMessage message={deployment.gitCommitMessage} />
          </ResourceDetailItem>
        </div>
        <div className="flex flex-col lg:pl-4 [&>*:not(:first-child)]:border-t [&>*:not(:first-child)]:border-black-100 border-t border-black-100 lg:border-t-0">
          <ResourceDetailItem title="Source">
            {source.id ? (
              <Link to={sourceDetailUrl(source.id)} className="text-indigo">
                {source.displayName}
              </Link>
            ) : (
              <span className="text-gray-500 italic">N/A</span>
            )}
          </ResourceDetailItem>
          <ResourceDetailItem title="Docker Image">
            <DockerImage
              image={deployment.dockerImage}
              digest={image.dockerRef}
              repoUrl={deployment.dockerRepositoryUrl}
            />
          </ResourceDetailItem>
        </div>
      </div>
    </Box>
  );
}

function NoteWithLinks({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return (
    <>
      {parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline"
          >
            {part}
          </a>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

const EXCLUDED_ACTIVITY_TYPES = new Set([
  "operation.logs",
  "app.logs",
  "database.logs",
  "database.tunnel",
  "image.scan",
  "llmkey.create",
  "llmkey.revoke",
  "vpntunnel.provision",
  "vpntunnel.deprovision",
]);

function DownloadLogsButton({
  logsContent,
  operationId,
}: {
  logsContent: string;
  operationId: string;
}) {
  if (!logsContent) {
    return (
      <ButtonIcon
        icon={<IconDownload variant="sm" />}
        variant="white"
        size="sm"
        disabled
      >
        Download Logs
      </ButtonIcon>
    );
  }

  // Create a blob URL from the log content for downloading
  const blob = new Blob([logsContent], { type: "text/plain" });
  const downloadUrl = URL.createObjectURL(blob);

  return (
    <ButtonLinkExternal
      href={downloadUrl}
      variant="white"
      size="sm"
      download={`operation-${operationId}-logs.txt`}
    >
      <IconDownload variant="sm" className="mr-2" />
      Download Logs
    </ButtonLinkExternal>
  );
}

function OperationLogsSection({ operationId }: { operationId: string }) {
  const dispatch = useDispatch();
  const op = useSelector((s) => selectOperationById(s, { id: operationId }));
  const action = useMemo(
    () => pollOperationById({ id: operationId, withDeleted: true }),
    [operationId],
  );
  const cancel = useMemo(() => cancelOpByIdPoll(), []);
  const [waiting, setWaiting] = useState(false);

  usePoller({ action, cancel });

  // Access the same cached logs that LogViewer is already fetching
  const logsAction = useMemo(
    () => fetchOperationLogs({ id: operationId }),
    [operationId],
  );
  const logsContent: string =
    useSelector((s) =>
      schema.cache.selectById(s, { id: logsAction.payload.key }),
    ) || "";

  useEffect(() => {
    if (op.status === "queued" || op.status === "running") {
      setWaiting(true);
    }

    if (
      op.status === "failed" ||
      op.status === "succeeded" ||
      op.status === "canceled"
    ) {
      dispatch(cancel);
      setWaiting(false);
    }
  }, [op.status, dispatch, cancel]);

  // If GET /operations/{id} returned 404, op won't be in store
  if (!op.id) {
    return null;
  }

  return (
    <>
      <hr className="my-6 border-[#E7E8E8]" />

      <div className="flex flex-row items-center justify-between mb-4">
        <div className="flex flex-row items-center">
          <h3 className="text-lg font-semibold text-gray-900">Logs</h3>
          <LoadingSpinner show={waiting} className="ml-2" />
        </div>
        <DownloadLogsButton
          logsContent={logsContent}
          operationId={operationId}
        />
      </div>
      <div className="w-full">
        <LogViewer op={op} />
      </div>
    </>
  );
}

export const ActivityDetailPage = () => {
  const { id = "" } = useParams();
  const activity = useSelector((s) => selectFreezerActivityById(s, { id }));

  // Try to fetch the operation if this might be an operation activity
  const operationId = activity.externalId || "";

  // ALWAYS call useQuery unconditionally (Rules of Hooks)
  // If operation ID is empty or invalid, API will handle it gracefully (404)
  // If the operation doesn't exist (404), it won't be added to the store
  useQuery(fetchOperationById({ id: operationId, withDeleted: true }));

  // Poll the primary resource (e.g., database, app, etc.) with deleted records included
  const resourceAction = useMemo(
    () =>
      pollResourceByTypeThunk({
        resourceType: activity.primaryResourceType || "",
        resourceId: activity.primaryResourceId || "",
        withDeleted: true,
      }),
    [activity.primaryResourceType, activity.primaryResourceId],
  );
  const resourceCancel = useMemo(() => cancelResourceByTypePoll(), []);

  usePoller({ action: resourceAction, cancel: resourceCancel });

  // Poll the activity itself until it reaches a terminal status
  const activityAction = useMemo(() => pollActivityById({ id }), [id]);
  const activityCancel = useMemo(() => cancelActivityByIdPoll(), []);

  usePoller({ action: activityAction, cancel: activityCancel });

  const dispatch = useDispatch();

  useEffect(() => {
    if (
      activity.id &&
      (activity.status === "succeeded" ||
        activity.status === "failed" ||
        activity.status === "canceled")
    ) {
      dispatch(activityCancel);
    }
  }, [activity.id, activity.status, dispatch, activityCancel]);

  // Cancel resource polling on unmount
  useEffect(() => {
    return () => {
      dispatch(resourceCancel);
    };
  }, [dispatch, resourceCancel]);

  // Check if the operation exists in the store (will be empty if API returned 404)
  const operation = useSelector((s) =>
    selectOperationById(s, { id: operationId }),
  );

  if (!activity.id) {
    return null;
  }

  // Only show operation logs if:
  // 1. Activity type is NOT in the excluded list
  // 2. The operation exists (was not a 404)
  const isOperationActivity =
    !EXCLUDED_ACTIVITY_TYPES.has(activity.activityType) && operation.id !== "";

  const appId =
    activity.primaryResourceType.toLowerCase() === "app"
      ? activity.primaryResourceId
      : (idForTargetResourceType(activity.targetResources, "app") ?? "");
  const isAppDeployment =
    APP_DEPLOYMENT_ACTIVITY_TYPES.has(activity.activityType) && appId !== "";

  return (
    <>
      {isAppDeployment && operationId ? (
        <DeploymentDetailsWidget appId={appId} operationId={operationId} />
      ) : null}
      <Box>
        {activity.summary ? (
          <>
            <div className="flex flex-row items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Additional Info
              </h3>
            </div>
            <div className="flex items-center text-base font-normal text-black break-all">
              {activity.summary}
            </div>
            <hr className="my-6 border-[#E7E8E8]" />
          </>
        ) : null}

        {activity.description?.note ? (
          <>
            <div className="flex flex-row items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Note</h3>
            </div>
            <div className="text-base font-normal text-black break-all">
              <NoteWithLinks text={String(activity.description.note)} />
            </div>
            <hr className="my-6 border-[#E7E8E8]" />
          </>
        ) : null}

        <div className="flex flex-row items-start mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Activity JSON</h3>
        </div>
        <JsonView src={activity as object} collapsed={false} />

        {isOperationActivity && (
          <OperationLogsSection operationId={operationId} />
        )}
      </Box>
    </>
  );
};
